import { LoaderFunctionArgs, redirect } from 'react-router-dom'
import { readAccountingCustomerContext } from '@/api/accounting-dealer/customer/read-customer-context'
import { readCreditCustomerContext } from '@/api/financial/customer/read-customer-context'
import { queryClient } from '@/api/react-query'
import { readUserProfile } from '@/api/user-manager'
import {
  companyRouteFor,
  hasSeenAnnouncement,
  isViewAs,
  readSelectedProduct,
  rememberSelectedCompany,
  setViewAsUserKey,
  clearViewAsUserKey,
  STORAGE_COMPANY_KEY,
  writeSelectedProduct,
} from '@/helpers'
import { routes } from '@/router'
import { nextProfileStep } from '@/pages/session/profile/profile-steps'
import { AccountingCustomerCompany } from '@/types/customer-context'

function redirectToCompany(company: AccountingCustomerCompany) {
  rememberSelectedCompany(company)
  return redirect(companyRouteFor(company))
}

export async function homeLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const searchParamToken = url.searchParams.get('token')

  if (searchParamToken) {
    localStorage.setItem('access_token', searchParamToken)
    queryClient.clear()

    const viewAsUserKey = url.searchParams.get('as')
    if (viewAsUserKey) {
      setViewAsUserKey(viewAsUserKey)
    } else {
      clearViewAsUserKey()
    }
  }

  const token = localStorage.getItem('access_token')

  if (!token) return null

  try {
    const user = await queryClient.ensureQueryData({
      queryKey: ['user-profile'],
      queryFn: readUserProfile,
    })

    if (!user) return null

    const viewAs = isViewAs()

    const isEmailVerified = user.alias?.find(
      (alias) => alias.type === 'email' && alias.verified,
    )
    if (!viewAs && !isEmailVerified)
      return redirect(routes.session.confirmEmail)
    if (!viewAs && !user.has_password)
      return redirect(routes.session.signup.password)

    const [accountingResult, creditResult] = await Promise.allSettled([
      queryClient.ensureQueryData({
        queryKey: ['customer-context', 'accounting'],
        queryFn: readAccountingCustomerContext,
      }),
      queryClient.ensureQueryData({
        queryKey: ['customer-context', 'financial'],
        queryFn: readCreditCustomerContext,
      }),
    ])

    const companies =
      accountingResult.status === 'fulfilled'
        ? accountingResult.value.companies
        : null
    const applications =
      creditResult.status === 'fulfilled'
        ? creditResult.value.applications
        : null

    const incompleteCompany = companies?.find(
      (company) =>
        company.deal_status === 'incomplete' && company.deal_step !== 'signed',
    )
    if (incompleteCompany) return redirectToCompany(incompleteCompany)

    if (
      accountingResult.status === 'fulfilled' &&
      creditResult.status === 'fulfilled' &&
      accountingResult.value.companies.length === 0 &&
      creditResult.value.applications.length === 0 &&
      !viewAs
    ) {
      const step = nextProfileStep(user)
      if (step) return redirect(routes.session.profile[step])
    }

    const hasAccounting = (companies?.length ?? 0) > 0
    const hasCredit = (applications?.length ?? 0) > 0

    if (
      hasAccounting &&
      hasCredit &&
      companies &&
      !hasSeenAnnouncement('product-picker-first-login', user.key)
    ) {
      const first =
        companies.find((company) => company.company_key) ?? companies[0]
      if (first.company_key) {
        localStorage.setItem(STORAGE_COMPANY_KEY, first.company_key)
      }
      return redirect(routes.accounting.index)
    }

    const selected = readSelectedProduct()

    if (selected?.product === 'credit' || !companies?.length) {
      writeSelectedProduct({ product: 'credit' })
      return redirect(routes.credit.index)
    }

    if (selected?.product === 'accounting') {
      const match = companies.find(
        (company) => company.company_key === selected.company_key,
      )
      if (match) return redirectToCompany(match)
    }

    if (companies.length === 1) return redirectToCompany(companies[0])

    return null
  } catch {
    return null
  }
}
